import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { getNodeMeta } from '../constants/nodeTypes';
import { EDGE_EVENT_LABELS } from '../constants/edgeEvents';

/* ─── Node width / row height constants ───────────────────────────── */
const NODE_W = 260;
const NODE_H = 64;
const H_GAP  = 80;   // horizontal gap between siblings
const V_GAP  = 100;  // vertical gap between levels

/**
 * BFS-based top-down auto-layout.
 * Returns a map of nodeId → { x, y }.
 *
 * Algorithm:
 *  1. BFS from root nodes (nodes with no incoming edge) to assign a level.
 *  2. Collect siblings per level, then space them evenly.
 */
function computeLayout(nodes, edges) {
  if (!nodes?.length) return {};

  const inDegree = {};
  nodes.forEach((n) => (inDegree[n.id] = 0));
  edges.forEach((e) => {
    if (e.to && inDegree[e.to] !== undefined) inDegree[e.to] += 1;
  });

  // BFS — assign depth level to every node
  const levelOf = {};
  const queue = nodes.filter((n) => inDegree[n.id] === 0).map((n) => n.id);
  queue.forEach((id) => (levelOf[id] = 0));

  let head = 0;
  while (head < queue.length) {
    const cur = queue[head++];
    edges
      .filter((e) => e.from === cur)
      .forEach((e) => {
        if (e.to && levelOf[e.to] === undefined) {
          levelOf[e.to] = levelOf[cur] + 1;
          queue.push(e.to);
        }
      });
  }

  // Fallback for disconnected nodes
  nodes.forEach((n) => {
    if (levelOf[n.id] === undefined) levelOf[n.id] = 0;
  });

  // Group by level
  const byLevel = {};
  nodes.forEach((n) => {
    const lv = levelOf[n.id];
    if (!byLevel[lv]) byLevel[lv] = [];
    byLevel[lv].push(n.id);
  });

  const positions = {};
  Object.entries(byLevel).forEach(([lv, ids]) => {
    const totalW = ids.length * NODE_W + (ids.length - 1) * H_GAP;
    ids.forEach((id, idx) => {
      positions[id] = {
        x: -(totalW / 2) + idx * (NODE_W + H_GAP),
        y: Number(lv) * (NODE_H + V_GAP),
      };
    });
  });

  return positions;
}

/* ─── Custom Node ─────────────────────────────────────────────────── */
function FlowNode({ data }) {
  const meta = getNodeMeta(data?.nodeType);
  const Icon = meta.icon;

  return (
    <div
      className="flow-canvas-node"
      style={{
        borderLeftColor: meta.color,
        boxShadow: data?.selected
          ? `0 0 0 2px ${meta.color}55, 0 4px 16px rgba(0,0,0,0.12)`
          : '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: meta.color, width: 10, height: 10, border: '2px solid #fff' }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: `${meta.color}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: meta.color,
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {Icon && <Icon />}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 12,
              color: '#1f2937',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 180,
            }}
          >
            {data?.config?._label || meta.listLabel?.(data?.config) || meta.label}
          </div>
          {data?.config?._label && (
            <div
              style={{
                fontSize: 10,
                color: '#9ca3af',
                marginTop: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 180,
              }}
            >
              {meta.listLabel?.(data?.config) || meta.label}
            </div>
          )}
          {!data?.config?._label && (
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>{data?.nodeType}</div>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: meta.color, width: 10, height: 10, border: '2px solid #fff' }}
      />
    </div>
  );
}

const nodeTypes = { flowNode: FlowNode };

/* ─── Edge label colours ──────────────────────────────────────────── */
const LABEL_COLOR = {
  matched:      '#7c3aed',
  user_input:   '#1d4ed8',
  valid:        '#15803d',
  invalid:      '#b91c1c',
  completed:    '#6b7280',
  yes:          '#15803d',
  no:           '#b91c1c',
  button_reply: '#0369a1',
  route:        '#d97706',
};

/* ─── Main Component ──────────────────────────────────────────────── */
/* ─── Inner Component ─────────────────────────────────────────────── */
const FlowCanvasInner = ({ definition, selectedNodeId, onSelectNode, onConnect }) => {
  const { setCenter } = useReactFlow();
  
  const rawNodes = useMemo(() => definition?.nodes || [], [definition?.nodes]);
  const rawEdges = useMemo(() => definition?.edges || [], [definition?.edges]);

  /* Compute layout from edge graph */
  const layout = useMemo(() => computeLayout(rawNodes, rawEdges), [rawNodes, rawEdges]);

  // Smooth pan/zoom to center the active node when selectedNodeId changes
  useEffect(() => {
    if (selectedNodeId && layout[selectedNodeId]) {
      const pos = layout[selectedNodeId];
      // Offset by half node width (260/2) and half node height (64/2) to center perfectly
      setCenter(pos.x + 130, pos.y + 32, { zoom: 1.2, duration: 800 });
    }
  }, [selectedNodeId, layout, setCenter]);

  const initialNodes = useMemo(
    () =>
      rawNodes.map((n) => ({
        id: n.id,
        type: 'flowNode',
        position: layout[n.id] ?? { x: 0, y: 0 },
        data: { nodeType: n.type, config: n.config, selected: n.id === selectedNodeId },
        selected: n.id === selectedNodeId,
        style: { width: NODE_W },
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawNodes, rawEdges, selectedNodeId]
  );

  const initialEdges = useMemo(
    () =>
      rawEdges
        .filter((e) => e.to)
        .map((e, i) => {
          const eventKey = e.event || 'completed';
          const labelColor = LABEL_COLOR[eventKey] || '#6b7280';
          return {
            id: `e-${e.from}-${eventKey}-${i}`,
            source: e.from,
            target: e.to,
            type: 'smoothstep',
            animated: true,
            label: EDGE_EVENT_LABELS[eventKey] || eventKey,
            labelStyle: {
              fontSize: 10,
              fontWeight: 600,
              fill: labelColor,
            },
            labelBgStyle: {
              fill: '#ffffff',
              fillOpacity: 0.95,
              rx: 4,
              ry: 4,
            },
            labelBgPadding: [4, 6],
            style: {
              stroke: labelColor,
              strokeWidth: 2,
              strokeDasharray: '5 4',
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: labelColor,
              width: 16,
              height: 16,
            },
          };
        }),
    [rawEdges]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => { setNodes(initialNodes); }, [initialNodes, setNodes]);
  useEffect(() => { setEdges(initialEdges); }, [initialEdges, setEdges]);

  const onNodeClick = useCallback(
    (_, node) => onSelectNode?.(node.id),
    [onSelectNode]
  );

  const handleConnect = useCallback(
    (params) => { onConnect?.(params.source, params.target, 'completed'); },
    [onConnect]
  );

  return (
    <div className="flow-builder-graph-wrap">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onConnect={handleConnect}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.25 }}
        minZoom={0.3}
        maxZoom={1.8}
        defaultEdgeOptions={{ type: 'smoothstep' }}
      >
        <Background gap={24} color="#e5e7eb" variant="dots" />
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={(n) => {
            const meta = getNodeMeta(n.data?.nodeType);
            return meta.color || '#d1d5db';
          }}
          style={{ background: '#f9fafb' }}
        />
      </ReactFlow>
    </div>
  );
};

/* ─── Main Component Wrapper ──────────────────────────────────────── */
const FlowCanvas = (props) => {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner {...props} />
    </ReactFlowProvider>
  );
};

export default FlowCanvas;
